import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ImageAnnotationElement, TupleOfstringAndstring } from '../../../api/api-client';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { InputText } from 'primeng/inputtext';
import { ViewDocumentRoutingInfo } from '../../../models/view-document-routing-info';

@Component({
  selector: 'app-view-document-dialog',
  imports: [
    InputText
  ],
  templateUrl: './view-document-dialog.component.html',
  styleUrl: './view-document-dialog.component.scss',
  changeDetection:ChangeDetectionStrategy.OnPush
})
export class ViewDocumentDialogComponent implements OnInit, AfterViewInit {

  @ViewChild('paintCanvas', {static: false}) canvas!: ElementRef<HTMLCanvasElement>;
  private context!: CanvasRenderingContext2D | null;
  private img!: HTMLImageElement;

  routingData!: ViewDocumentRoutingInfo;
  pdfUrl!:SafeResourceUrl;

  constructor(public ref: DynamicDialogRef, private dialogService: DialogService, private sanitizer: DomSanitizer) {
    this.routingData = this.dialogService.getInstance(this.ref)?.data;
  }

  ngOnInit(): void {
    if (!this.routingData.isSkeleton) {
      if (this.routingData.document.documentType == 'Document') {
        const byteCharacters = atob(this.routingData.document.base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.codePointAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob)+"#toolbar=0&navpanes=0");
      }
    }
  }

  ngAfterViewInit() {
    if (this.routingData.isSkeleton) {
      this.img = new Image();
      this.img.loading = 'eager'
      this.img.src = `img/skeleton_${this.routingData?.species?.toLowerCase()}.svg`
      this.context = this.canvas.nativeElement.getContext('2d');
      this.img.onload = () => {
        if (this.context) {
          this.resizeCanvas();
          this.drawAnnotations();
        }
      }
    } else if (this.routingData.document.documentType == 'Image') {
      this.img = new Image();
      this.img.loading = 'eager'
      this.img.src = 'data:' + this.routingData.document.mimeType + ';base64,' + this.routingData.document.base64Data
      this.context = this.canvas.nativeElement.getContext('2d');
      this.img.onload = () => {
        if (this.context) {
          this.resizeCanvas();
          this.drawAnnotations();
        }
      }
    }
  }

  resizeCanvas(){
    this.canvas.nativeElement.width = this.img.naturalWidth;
    this.canvas.nativeElement.height = this.img.naturalHeight;
    if(this.context)
    this.context.drawImage(this.img, 0, 0);
  }

  drawAnnotations() {
    if(this.routingData.isSkeleton){
      for (let annotation of this.routingData.skeletonAnnotation?.annotationElements ?? []) {
        switch (annotation.type) {
          case 'Circle':
            this.drawCircle(annotation);
            break;
          case 'Freehand':
            this.drawFreehand(annotation);
            break;
          default:
            break;
        }
      }
    }
    else if (this.routingData.document.annotation?.annotationElements) {
      for (let annotation of this.routingData.document.annotation?.annotationElements ?? []) {
        switch (annotation.type) {
          case 'Circle':
            this.drawCircle(annotation);
            break;
          case 'Freehand':
            this.drawFreehand(annotation);
            break;
          default:
            break;
        }
      }
    }
  }

  drawCircle(annotation: ImageAnnotationElement) {
    if (this.context &&
      annotation.circleCenter?.x &&
      annotation.circleCenter.y &&
      annotation.circleRadiusPx) {
      this.setupCanvas(annotation);
      this.context.beginPath();
      this.context.globalCompositeOperation = 'source-over';
      this.context.arc(annotation.circleCenter?.x, annotation.circleCenter?.y, annotation.circleRadiusPx, 0, 2 * Math.PI);
      this.context.stroke();
    }
  }

  drawFreehand(annotation: ImageAnnotationElement) {
    if (this.context &&
      annotation.freehandPoints) {
      this.setupCanvas(annotation);
      this.context.beginPath();
      this.context.globalCompositeOperation = 'source-over';
      for (let freehandPoint of annotation.freehandPoints)
        if (freehandPoint.x && freehandPoint.y) {
          this.context.moveTo(freehandPoint.x, freehandPoint.y);
          this.context.lineTo(freehandPoint.x, freehandPoint.y);
          this.context.stroke();
        }
    }
  }

  private setupCanvas(annotation: ImageAnnotationElement) {
    if (this.context) {
      this.context.lineCap = 'round';
      this.context.lineWidth = annotation.thicknessPx ?? 1
      this.context.strokeStyle = annotation.color ?? 'black';
    }
  }

  sanitizeResourceString(value: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(value);
  }

  protected getInputId(legendEntry: TupleOfstringAndstring) {
    return 'text' + (legendEntry.x ?? '').toString()
  }
}
